import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { TrackingCustomEvents } from '@/constants/constants';
import { sendMessageToSentry } from '@/lib/sentry';
import { setPageTitle, trackCustomEvent, trackException } from '@/lib/appInsights';
import { Author, Product } from '@/types/models.types';
import { ArticlesWithMeta, CMSContextType, DashboardData, PaywallContextType } from '@/types/util.types';

import { useCms } from '@/hooks/useCms';
import { usePaywall } from '@/hooks/usePaywall';
import { useAuthorTabs } from '@/hooks/useAuthorTabs';
import { useUser } from '@/state/UserContext';
import AuthorProfileSkeleton from '@/components/skeletons/AuthorProfileSkeleton';
import SubscribeButton from '@/components/SubscribeButton';
import MetaTags from '@/components/MetaTags';
import AuthorBio from '@/components/author/AuthorBio';
import AuthorContentTabs from '@/components/author/AuthorContentTabs';
import AuthorTabContent from '@/components/author/AuthorTabContent';
import { hasUserSubscribed } from '@/utils/hasUserSubscribed';

const defaultAuthorArticles: ArticlesWithMeta = {
  articles: [],
  meta: {
    pagination: {
      page: 1,
      pageSize: 0,
      pageCount: 0,
      total: 0,
    },
  },
};

const AuthorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { email, userId } = useUser();
  const { getAuthorById, getArticlesByAuthor, getDashboardsByAuthor } = useCms() as CMSContextType;
  const { getAuthorProducts, subscriptions, hasUserCompletedTrial, userHasBypass } = usePaywall() as PaywallContextType;
  const [author, setAuthor] = useState<Author>();
  const [authorArticles, setAuthorArticles] = useState<ArticlesWithMeta>(defaultAuthorArticles);
  const [authorDashboards, setAuthorDashboards] = useState<DashboardData>();
  const [products, setProducts] = useState<Product>();
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const showSubscribeButton = !userHasBypass && products && !isSubscribed;

  const { activeTab, availableTabs, getContentCount, getContentLabel, getActiveTabIcon, handleTabChange } =
    useAuthorTabs({
      authorId: author?.lookupKey,
      authorArticles,
      authorDashboards,
    });

  /**
   * Gets the product ID and sends the user to the checkout page
   */
  const handleSubscribe = () => {
    if (products === undefined || author === undefined) return;
    const showTrial = getPaywallMode(author.lookupKey) === 'trial';

    trackCustomEvent(TrackingCustomEvents.click.author.subscribe, {
      author_id: author.lookupKey.toString(),
      checkout_mode: showTrial ? 'trial' : 'subscribe',
      user_auth_status: isAuthenticated ? 'logged_in' : 'anonymous',
      user_email: email ?? 'anonymous',
      user_id: userId ?? 'anonymous',
    });

    navigate('/subscribe', {
      state: { productId: products.price.id, authorId: id, isTrial: showTrial },
    });
  };

  /*
   * Handles the unsubscribe button click
   */
  const handleUnsubscribe = () => {
    if (author === undefined) return;
    trackCustomEvent(TrackingCustomEvents.click.author.unsubscribe, {
      author_id: author.lookupKey.toString(),
      user_auth_status: isAuthenticated ? 'logged_in' : 'anonymous',
      user_email: email ?? 'anonymous',
      user_id: userId ?? 'anonymous',
    });
    navigate('/me/purchases');
  };

  /**
   * Handles the login button click
   */
  const handleLogin = () => {
    trackCustomEvent(TrackingCustomEvents.click.author.login);
    sendMessageToSentry('Event: Author login button clicked');
    instance.loginRedirect();
  };

  /*
   * Sets the paywall mode to ensure the right checkout flow is used
   */
  const getPaywallMode = (authorId: number) => {
    if (!products) {
      return 'purchase';
    }
    if (products.trialEnabled) {
      const outcome = hasUserCompletedTrial(authorId);
      return outcome ? 'subscribe' : 'trial';
    }
    return 'subscribe';
  };

  /**
   * Fetch author data and articles.
   */
  useEffect(() => {
    const fetchAuthor = async () => {
      if (id === undefined) return;

      try {
        const author = await getAuthorById(parseInt(id));
        if (!author) {
          trackException(new Error(`Author ${id} not found`));
          navigate('/not-found');
          return;
        }
        setAuthor(author);
        setPageTitle(author.name, 'author');

        const response = await getArticlesByAuthor(parseInt(id));
        setAuthorArticles(response);
      } catch (error) {
        console.error('Error fetching author data:', error);
        setAuthorArticles(defaultAuthorArticles);
      }
    };

    fetchAuthor();
  }, [id, getAuthorById, getArticlesByAuthor, navigate, subscriptions]);

  /**
   * Fetch products, subscriptions and dashboard data
   */
  useEffect(() => {
    const fetchProductsAndSubscriptions = async () => {
      if (id === undefined) return;
      if (author === undefined || inProgress !== InteractionStatus.None) return;

      try {
        const _products = await getAuthorProducts(author.lookupKey);
        setProducts(_products && _products.length > 0 ? _products[0] : undefined);

        if (subscriptions.length > 0) {
          setIsSubscribed(hasUserSubscribed(id, subscriptions));
        } else {
          setIsSubscribed(false);
        }

        const dashboards = await getDashboardsByAuthor(author.lookupKey);
        setAuthorDashboards(dashboards);
      } catch (error) {
        console.error('Error fetching products or subscriptions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductsAndSubscriptions();
  }, [id, author, getAuthorProducts, getDashboardsByAuthor, inProgress, subscriptions]);

  if (isLoading) {
    return <AuthorProfileSkeleton />;
  }

  return (
    <>
      {author && <MetaTags title={author.name} description={author.bio} imageUrl={author.image} />}

      {author ? (
        <div className='mb-8 mx-auto print:hidden'>
          {/* Author banner image */}
          {author.bannerImage && (
            <div className='md:mx-auto'>
              <div className='relative w-full'>
                <img
                  className='object-cover mx-0 w-[100vw] lg:w-full h-full aspect-3/1 max-h-[300px] lg:mx-auto'
                  src={author.bannerImage}
                  alt='banner-image'
                />
              </div>
            </div>
          )}

          <div className='container mx-auto px-4'>
            {/* Author header */}
            <div className='flex flex-col md:flex-row items-center pt-8 pb-14 md:pb-8 mx-auto border-b'>
              {author.image && (
                <img
                  data-cy='author-profile-image'
                  className='w-16 h-16 shadow-xl object-cover rounded-full md:mr-4'
                  src={author.image}
                  alt='Profile'
                />
              )}
              <div className='flex-1'>
                <div className='flex flex-col md:flex-row justify-between'>
                  <div className='text-center mt-4 md:text-left md:mt-0 md:flex md:flex-col md:justify-center'>
                    <h3 data-cy='author-profile-name' className='font-bold -mb-1 text-[#002327]'>
                      {author.name}
                    </h3>
                    <p className='text-slate-500'>{author.organisation}</p>
                    <p className='text-slate-500'>
                      {author.location} | {author.timezone}
                    </p>
                    {author.website && (
                      <a
                        href={author.website}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-slate-500 hover:underline hover:text-teal-600'
                      >
                        {author.website}
                      </a>
                    )}
                  </div>

                  <div className='flex flex-col mt-8 text-zinc-500 text-center md:mt-0 items-center justify-center'>
                    {showSubscribeButton && (
                      <SubscribeButton
                        isSubscribed={isSubscribed}
                        price={products.price.amount}
                        currency={products.price.currency}
                        trialEnabled={products.trialEnabled}
                        hasUserCompletedTrial={hasUserCompletedTrial(author.lookupKey)}
                        onClick={!isAuthenticated ? handleLogin : isSubscribed ? handleUnsubscribe : handleSubscribe}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Author biography */}
            <AuthorBio author={author} />

            {/* Author content with tabs */}
            <div className='py-8'>
              <div className='flex justify-between'>
                <AuthorContentTabs
                  availableTabs={availableTabs}
                  activeTab={activeTab}
                  handleTabChange={handleTabChange}
                />

                <div className='flex items-center text-gray-400'>
                  {getActiveTabIcon()}
                  <span style={{ verticalAlign: 'middle', display: 'inline-block', marginLeft: '5px' }}>
                    {getContentCount()} {getContentLabel()}
                  </span>
                </div>
              </div>
            </div>

            {/* Tab content */}
            <div data-cy='author-content-container'>
              <AuthorTabContent
                activeTab={activeTab}
                articles={authorArticles.articles}
                dashboardData={authorDashboards}
                buttonConfig={{
                  isAuthenticated,
                  isSubscribed,
                  products: products || null,
                  authorName: author.name,
                  hasUserCompletedTrial: hasUserCompletedTrial(author.lookupKey),
                  onClick: !isAuthenticated ? handleLogin : handleSubscribe,
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <AuthorProfileSkeleton />
      )}
    </>
  );
};

export default AuthorPage;
